import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { toast } from 'sonner';
import { Users, Shield } from 'lucide-react';

const TeamTab = ({ user, team }) => {
  const { API, getAuthHeaders } = useAuth();
  const [roles, setRoles] = useState({});
  const [saving, setSaving] = useState(null);

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';

  const changeRole = async (member, role) => {
    setRoles(prev => ({ ...prev, [member.id]: role }));
    setSaving(member.id);
    try {
      await axios.put(`${API}/team/${member.id}/role?role=${role}`, {}, getAuthHeaders());
      toast.success(`${member.name} ora è ${role === 'admin' ? 'amministratore' : role === 'manager' ? 'manager' : 'dipendente'}`);
    } catch (err) {
      setRoles(prev => ({ ...prev, [member.id]: member.role }));
      toast.error(err.response?.data?.detail || 'Cambio ruolo non riuscito');
    } finally {
      setSaving(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-outfit">Membri del Team</CardTitle>
        <CardDescription>Gestisci il tuo team e i loro ruoli</CardDescription>
      </CardHeader>
      <CardContent>
        {user?.role === 'admin' ? (
          team.length > 0 ? (
            <div className="space-y-3">
              {team.map((member) => (
                <div key={member.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 rounded-lg border border-border"
                  data-testid={`team-member-${member.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar className="shrink-0">
                      <AvatarFallback className="bg-primary/10 text-primary">{getInitials(member.name)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="font-medium break-words">{member.name}</p>
                      <p className="text-sm text-muted-foreground break-all">{member.email}</p>
                    </div>
                  </div>
                  <Select value={roles[member.id] || member.role} disabled={saving === member.id}
                    onValueChange={(v) => changeRole(member, v)}>
                    <SelectTrigger className="w-full sm:w-40 min-h-[44px] shrink-0"
                      data-testid={`team-role-${member.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Amministratore</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="staff">Dipendente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">Nessun membro del team</p>
            </div>
          )
        ) : (
          <div className="text-center py-8">
            <Shield className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Accesso amministratore richiesto per gestire il team</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamTab;
